import React, { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { Typography } from "@material-ui/core";

import PasswordTextField from "./PasswordTextField";

const useStyles = makeStyles((theme) => ({
  passwordRequirements: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  passwordRequirement: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.5),
  },
  passwordRequirementValid: {
    color: "#2e7d32",
  },
  passwordRequirementInvalid: {
    color: "#c62828",
  },
}));

const getPasswordCharacters = (password) => Array.from(password);
const isLetter = (char) => char.toLowerCase() !== char.toUpperCase();
const isNumber = (char) => /\d/.test(char);
const isUppercaseLetter = (char) =>
  isLetter(char) && char === char.toUpperCase();
const isLowercaseLetter = (char) =>
  isLetter(char) && char === char.toLowerCase();
const isSpecialCharacter = (char) =>
  !isLetter(char) && !isNumber(char) && !/\s/.test(char);

const passwordRequirements = [
  {
    label: "Al menos una mayúscula",
    isValid: (password) =>
      getPasswordCharacters(password).some(isUppercaseLetter),
  },
  {
    label: "Al menos una minúscula",
    isValid: (password) =>
      getPasswordCharacters(password).some(isLowercaseLetter),
  },
  {
    label: "Al menos un carácter especial",
    isValid: (password) =>
      getPasswordCharacters(password).some(isSpecialCharacter),
  },
  {
    label: "Al menos un número",
    isValid: (password) => /\d/.test(password),
  },
  {
    label: "Entre 8 y 20 caracteres",
    isValid: (password) => password.length >= 8 && password.length <= 20,
  },
];

export const getPasswordValidation = (password) =>
  passwordRequirements.map((requirement) => ({
    ...requirement,
    valid: requirement.isValid(password),
  }));

export const isPasswordStrongEnough = (password) =>
  getPasswordValidation(password).every((requirement) => requirement.valid);

export function passwordsMatch(firstPassword, secondPassword) {
  const longestLength = Math.max(firstPassword.length, secondPassword.length);
  let difference = firstPassword.length ^ secondPassword.length;

  for (let index = 0; index < longestLength; index += 1) {
    difference |=
      (firstPassword.charCodeAt(index) | 0) ^
      (secondPassword.charCodeAt(index) | 0);
  }

  return difference === 0;
}

export default function PasswordCreationFields({
  password,
  confirmation,
  onPasswordChange,
  onConfirmationChange,
}) {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const passwordValidation = getPasswordValidation(password);
  const passwordHasValue = password.length > 0;
  const confirmationHasValue = confirmation.length > 0;
  const passwordIsValid = isPasswordStrongEnough(password);
  const passwordsMatchConfirmation = passwordsMatch(password, confirmation);

  return (
    <>
      <PasswordTextField
        id="password"
        name="password"
        label="Contraseña"
        required
        variant="outlined"
        margin="dense"
        value={password}
        onChange={onPasswordChange}
        isVisible={showPassword}
        onVisibilityChange={setShowPassword}
        error={passwordHasValue && !passwordIsValid}
        fullWidth
      />
      <PasswordTextField
        id="confirmPassword"
        name="confirmPassword"
        label="Confirmar contraseña"
        required
        variant="outlined"
        margin="dense"
        value={confirmation}
        onChange={onConfirmationChange}
        isVisible={showPassword}
        onVisibilityChange={setShowPassword}
        helperText={
          confirmationHasValue && !passwordsMatchConfirmation
            ? "Las contraseñas ingresadas no coinciden"
            : ""
        }
        error={confirmationHasValue && !passwordsMatchConfirmation}
        fullWidth
      />
      <div className={classes.passwordRequirements}>
        {passwordValidation.map((requirement) => (
          <Typography
            key={requirement.label}
            className={`${classes.passwordRequirement} ${
              requirement.valid
                ? classes.passwordRequirementValid
                : classes.passwordRequirementInvalid
            }`}
          >
            {requirement.valid ? "✓" : "✕"} {requirement.label}
          </Typography>
        ))}
      </div>
    </>
  );
}
